import { downloadFile } from '@/utils/download-file'
import { printFile } from '@/utils/print-file'

import useMutationWithErrorsHandling from './useMutationWithErrorsHandling'

export type DownloadParams = {
  columnsKey?: string[]
  format?: 'xlsx' | 'pdf'
  isPrintEnabled?: boolean
}

type Props<TParams extends DownloadParams> = {
  fileName: string
  requestParams: TParams
  downloadMutation: (params: TParams) => Promise<File>
}

export const useDownload = <TParams extends DownloadParams>(
  props: Props<TParams>
) => {
  const { mutate: download, isPending: isDownloadLoading } =
    useMutationWithErrorsHandling({
      mutationFn: async (params: DownloadParams & TParams) => {
        const file = await props.downloadMutation(params)

        return file
      },
      onSuccess: (data, payload) => {
        if (payload.isPrintEnabled) {
          printFile(data)
        } else {
          const name = `${props.fileName}.${payload.format}`
          downloadFile({ fileData: data, fileName: name })
        }
      }
    })

  const onXlsExport = () => {
    download({
      ...props.requestParams,
      format: 'xlsx'
    })
  }

  const onPdfExport = () => {
    download({
      ...props.requestParams,
      format: 'pdf'
    })
  }

  const onPrint = () => {
    download({
      ...props.requestParams,
      isPrintEnabled: true,
      format: 'pdf'
    })
  }

  return {
    onXlsExport,
    onPdfExport,
    onPrint,
    isDownloadLoading
  }
}

export default useDownload
